import { createTypedClient, tokenStore } from '@/lib/http-core';
import type { paths } from '@/lib/api-admin';

interface RefreshEnvelope {
  success: boolean;
  data?: { accessToken?: string };
  error?: unknown;
}

/**
 * C-2: 만료된 access token 을 `everline_admin_refresh` HttpOnly 쿠키로 재발급.
 * BE 계약 = `/admin/v1/auth/refresh` (gateway → user-service). 새 access token 을
 * tokenStore 에 영속시켜 미들웨어의 재시도뿐 아니라 후속 요청도 갱신 토큰을 쓴다.
 *
 * C-1 보강: 세션 이력이 없으면(비로그인) refresh 자체를 시도하지 않는다 — 미들웨어가
 * 1차 게이트지만, 직접 호출 경로도 동일하게 무해화한다. 단, `force` 는 부팅 시 1회
 * cold-start 복구(아래 `bootstrapAuth`)를 위해 이 가드를 우회한다: 새 탭/세션스토리지
 * 소실 상황에선 hadSession 이 false 라도 유효한 refresh 쿠키가 남아 있을 수 있으므로
 * 명시적 1회 시도가 필요하다. 진짜 익명 방문자는 401 → null → /login 으로 귀결된다.
 */
const refreshAccessToken = async (force = false): Promise<string | null> => {
  if (!force && !tokenStore.hadSession) return null;
  try {
    const response = await fetch('/admin/v1/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) return null;
    const env = (await response.json()) as RefreshEnvelope;
    if (env.success !== true) return null;
    const accessToken = env.data?.accessToken ?? null;
    if (accessToken) tokenStore.set(accessToken);
    return accessToken;
  } catch {
    return null;
  }
};

/**
 * Cold-start silent re-auth (BO 진단 사이클 — 새 탭/세션스토리지 소실 복구).
 *
 * access token 은 탭 단위 sessionStorage 에, refresh 토큰은 cross-tab HttpOnly
 * 쿠키(`everline_admin_refresh`)에 있다. 따라서 새 탭이나 sessionStorage 가 비워진
 * cold load 에서는 메모리 토큰이 없어 라우트 가드가 곧장 /login 으로 보내, 유효한
 * refresh 쿠키가 한 번도 사용되지 않는다(라이브 재현됨). 부팅 시 1회 refresh 를
 * 시도해 쿠키만으로 세션을 복구한다.
 *
 * - 정확히 1회만 실행한다(promise 캐시). 가드/컴포넌트가 여러 번 await 해도 안전.
 * - refresh 는 `tokenStore.withRefreshLock` 의 단일 in-flight promise 를 거치므로,
 *   부팅 refresh 와 첫 요청의 401 refresh 가 동시에 일어나도 회전(rotation)은 1회뿐
 *   → 동시 double-spend 401 재발 방지.
 * - 이미 토큰이 있으면(같은 탭 내 정상 네비게이션) no-op.
 */
let bootstrapPromise: Promise<string | null> | null = null;

export const bootstrapAuth = (): Promise<string | null> => {
  if (tokenStore.token) return Promise.resolve(tokenStore.token);
  if (bootstrapPromise) return bootstrapPromise;
  bootstrapPromise = tokenStore.withRefreshLock(() => refreshAccessToken(true));
  return bootstrapPromise;
};

/** Test-only — reset the one-shot bootstrap latch between cases. */
export const __resetBootstrapAuth = (): void => {
  bootstrapPromise = null;
};

const LOGIN_PATH = '/login';

const onAuthLost = (): void => {
  tokenStore.clear();
  if (typeof window === 'undefined') return;
  if (!window.location.pathname.startsWith(LOGIN_PATH)) {
    window.location.assign(LOGIN_PATH);
  }
};

export const apiClient = createTypedClient<paths>({
  baseUrl: '',
  channel: 'ADMIN',
  refreshAccessToken,
  onAuthLost,
});

/**
 * Auth headers for endpoints not yet in the generated OpenAPI spec.
 *
 * ★`adminFetch` 가 내부에서 쓰는 것이고, **호출부에서 직접 쓰지 않는다.** 이 헬퍼를 꺼내
 * raw `fetch` 에 얹는 순간 아래 401 복구가 통째로 우회된다 — 그게 바로 엑셀 다운로드가 깨졌던
 * 원인이다. `src/__tests__/noRawFetch.test.ts` 가 그 조합을 막는다.
 */
export const adminHeaders = (): HeadersInit =>
  tokenStore.token
    ? { Authorization: `Bearer ${tokenStore.token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };

/**
 * QA-163 — 만료 토큰 401 자동복구 fetch (generated OpenAPI 미반영 raw 엔드포인트용).
 *
 * 타입드 클라이언트(`apiClient`)는 미들웨어로 401→single-flight refresh→1회 retry 를
 * 수행하지만, `adminHeaders()` + raw `fetch` 직접 호출 경로는 이 보호를 우회한다. 긴 편집
 * 세션(예: 해외 배송비 200+ 소수점 요율 입력) 도중 access token 이 만료되면 저장 PUT 이 stale
 * 토큰으로 나가 **401 로 하드 실패**한다("overseas policy update failed: 401").
 *
 * 2026-09-23 에 관리자 API 호출부 약 250곳을 전부 이 헬퍼로 옮겼다. 다시 새어 나오지 않도록
 * `src/__tests__/noRawFetch.test.ts` 가 raw `fetch` 를 막는다(정당한 예외는 그 목록에 사유와
 * 함께 등록한다).
 *
 * 본 헬퍼는 미들웨어와 동일한 규칙으로 그 경로를 보호한다:
 *  - 요청에 `adminHeaders()` 자동 부착(호출부에서 headers 를 또 주면 그게 우선).
 *  - 401 + 세션 이력 있으면 `withRefreshLock(refreshAccessToken)` 단일회전 후 1회 retry.
 *  - 세션-인증 경로가 아닌 곳의 retry-후-401 = RBAC 거부(403 의미)라 로그아웃하지 않는다.
 *  - refresh 실패(터미널)일 때만 onAuthLost(단일 latch). 익명(hadSession=false)은 refresh 생략.
 *
 * Idempotency-Key 등 추가 헤더는 호출부에서 spread 로 합쳐 넘긴다(adminHeaders 와 병합).
 */
const SESSION_AUTH_PATH_REGEX = /\/(?:admin\/)?v\d+\/(?:auth\/|me(?:$|\/))/;

const isSessionAuthUrl = (url: string): boolean => {
  try {
    return SESSION_AUTH_PATH_REGEX.test(new URL(url, window.location.origin).pathname);
  } catch {
    return SESSION_AUTH_PATH_REGEX.test(url);
  }
};

/**
 * 헤더 병합. **spread 로 합치면 안 된다** — `HeadersInit` 은 plain object 말고도 `Headers`
 * 인스턴스와 `[key, value][]` 를 허용하는데, `Headers` 는 열거 가능한 자체 속성이 없어
 * `{ ...new Headers({ 'Idempotency-Key': 'k' }) }` 가 **조용히 `{}` 가 된다**(멱등키 유실).
 * Headers API 로 정규화해 어떤 형태로 넘겨도 동작하게 한다. 호출부 헤더가 기본값을 덮는다.
 */
const mergeHeaders = (base: HeadersInit, extra?: HeadersInit): Record<string, string> => {
  const merged = new Headers(base);
  if (extra) new Headers(extra).forEach((value, key) => merged.set(key, value));
  const out: Record<string, string> = {};
  merged.forEach((value, key) => {
    out[key] = value;
  });
  return out;
};

/**
 * multipart 업로드는 브라우저가 boundary 와 함께 Content-Type 을 직접 설정해야 한다.
 * `adminHeaders()` 의 `application/json` 이 남아 있으면 boundary 가 빠져 서버가 파트를 파싱하지
 * 못한다(슬롯 엑셀 일괄 업로드·상품 import 등). body 가 FormData 면 Content-Type 만 빼고
 * Authorization 은 유지한다 — 호출부가 직접 지우던 처리를 래퍼가 담당한다.
 *
 * (Headers 정규화를 거치면 키가 소문자라 `content-type` 만 지우면 되지만, 직접 구성한 객체가
 * 들어올 경우까지 고려해 두 표기를 모두 지운다.)
 */
const headersForBody = (merged: Record<string, string>, body: BodyInit | null | undefined) => {
  if (!(typeof FormData !== 'undefined' && body instanceof FormData)) return merged;
  const next = { ...merged };
  delete next['Content-Type'];
  delete next['content-type'];
  return next;
};

export const adminFetch = async (input: string, init: RequestInit = {}): Promise<Response> => {
  const headers = headersForBody(mergeHeaders(adminHeaders(), init.headers), init.body);
  const res = await fetch(input, { ...init, headers, credentials: 'include' });
  if (res.status !== 401) return res;

  // 익명(세션 이력 없음)은 refresh 노이즈/리다이렉트 루프 방지 — 401 그대로 반환.
  if (!tokenStore.hadSession) return res;

  const newToken = await tokenStore.withRefreshLock(() => refreshAccessToken());
  if (!newToken) {
    tokenStore.clear();
    if (tokenStore.markAuthLostOnce()) onAuthLost();
    return res;
  }

  // 갱신 토큰으로 1회 재시도. Authorization 만 새 토큰으로 덮고 나머지 헤더는 유지.
  //   ★spread 로 덮으면 안 된다 — headers 는 Headers 정규화를 거쳐 소문자 키(`authorization`)라
  //   `Authorization` 을 얹으면 두 항목이 공존해 stale 토큰이 함께 나갈 수 있다. mergeHeaders 는
  //   대소문자를 같은 헤더로 취급해 확실히 교체한다.
  const retryHeaders = mergeHeaders(headers, { Authorization: `Bearer ${newToken}` });
  const retried = await fetch(input, { ...init, headers: retryHeaders, credentials: 'include' });
  // 재시도 후에도 401 이고 세션-인증 경로면 진짜 세션 소실 → 로그아웃. 그 외(배송비 등)는
  // RBAC 거부(403 의미)라 로그아웃하지 않고 호출부가 친절 메시지로 처리하게 둔다.
  if (retried.status === 401 && isSessionAuthUrl(input)) {
    tokenStore.clear();
    if (tokenStore.markAuthLostOnce()) onAuthLost();
  }
  return retried;
};
